"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, MapPin, ArrowRight, ShieldCheck, Sparkles, PhoneCall, Star } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
const trustSignals = [{
  text: "100% Direct Access to Yassine"
}, {
  text: "Tailored Carlisle & Cumbria Focus"
}, {
  text: "Google Map Pack Optimization"
}, {
  text: "Transparent Reporting & Clear Deliverables"
}];
const kpiHighlights = [{
  number: "100%",
  label: "Direct Consultant Contact"
}, {
  number: "#1",
  label: "Local Map Pack Focus"
}, {
  number: "0",
  label: "Agency Lock-in Contracts"
}];
export default function Carlisle() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localherocarlisle" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-28">
      <div className="pointer-events-none absolute -top-24 -right-24 size-96 rounded-full bg-accent/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 size-96 rounded-full bg-secondary/20 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:col-span-7 space-y-6 md:space-y-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 px-3.5 py-1.5 text-xs sm:text-sm font-medium backdrop-blur-sm">
              <MapPin className="size-4 text-accent" />
              <span>Carlisle &amp; Cumbria Local Search Specialist</span>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Local SEO Consultant in Carlisle
            </h2>

            <p className="font-sans text-base sm:text-lg md:text-xl text-primary-foreground/90 max-w-2xl leading-relaxed">
              I help Carlisle and Cumbria businesses rank in Google search results and capture high-intent local customers. When you hire YE Consultancy, you work 100% directly with me, Yassine Essaadi — no junior account managers, no outsourced templates, and no fluff.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg font-semibold text-base h-12 px-6 rounded-lg transition-[color,background-color,box-shadow]">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent font-medium text-base h-12 px-6 rounded-lg transition-[color,background-color]">
                <Link to="/CaseStudiesPage">Explore My Case Studies</Link>
              </Button>
            </div>

            <div className="pt-4 border-t border-primary-foreground/15">
              <p className="text-xs sm:text-sm font-medium text-primary-foreground/80 mb-3 flex items-center gap-2">
                <ShieldCheck className="size-4 text-accent" />
                {"Why local Carlisle businesses choose to work directly with me:"}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {trustSignals.map((item, i) => <div data-index={i} key={i} className="flex items-center gap-2 text-sm text-primary-foreground/90">
                    <CheckCircle2 className="size-4 shrink-0 text-accent" />
                    <span>{item.text}</span>
                  </div>)}
              </div>
            </div>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="lg:col-span-5">
            <div className="relative mx-auto max-w-md lg:max-w-none rounded-2xl bg-card text-card-foreground p-6 sm:p-8 shadow-2xl border border-border">
              <div className="flex items-center gap-4 pb-6 border-b border-border">
                <SafeHydrate><Avatar className="size-16 sm:size-20 border-2 border-primary shadow-md">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_carlisle_seo.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_carlisle_seo.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_carlisle_seo.png-wvc-sizes" alt="Yassine Essaadi - Independent Freelance SEO Consultant in Carlisle" loading="lazy" decoding="async" />
                  <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-lg">
                    YE
                  </AvatarFallback>
                </Avatar></SafeHydrate>
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground font-medium text-xs">
                      Independent Consultant
                    </Badge>
                  </div>
                  <h2 className="font-serif text-xl font-bold text-card-foreground">
                    Yassine Essaadi
                  </h2>
                  <p className="text-xs sm:text-sm text-muted-foreground font-medium">
                    Founder &amp; SEO Strategy Expert
                  </p>
                </div>
              </div>

              <div className="py-6 space-y-4">
                <div className="flex items-start gap-3 rounded-xl bg-muted/60 p-3.5 border border-border/50">
                  <Sparkles className="size-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-card-foreground leading-relaxed">
                    <span className="font-semibold text-primary">Direct Expertise:</span> {"I manage every technical audit, keyword map, and local Google Map Pack campaign personally for Carlisle businesses."}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center pt-2">
                  {kpiHighlights.map((stat, i) => <div data-index={i} key={i} className="p-2 sm:p-3 rounded-lg bg-background border border-border shadow-2xs">
                      <div className="font-serif text-lg sm:text-xl font-bold text-primary">
                        {stat.number}
                      </div>
                      <div className="text-[11px] sm:text-xs text-muted-foreground leading-tight mt-0.5">
                        {stat.label}
                      </div>
                    </div>)}
                </div>
              </div>

              <div className="pt-4 border-t border-border flex items-center justify-between text-xs sm:text-sm">
                <div className="flex items-center gap-1 text-muted-foreground">
                  <Star className="size-4 fill-amber-400 text-amber-400" />
                  <span className="font-semibold text-card-foreground">Genuine Search Results</span>
                </div>
                <a href="tel:+447490984430" className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline">
                  <PhoneCall className="size-3.5" />
                  +44 7490984430
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}